import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  Text,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react'
import { z } from 'zod'
import { useAuthStore } from '@/store/auth'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export function ForgotPasswordForm() {
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const { forgotPassword, isLoading } = useAuthStore()
  const colors = useThemeColors()
  const { settings: siteSettings } = useSiteSettings()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setError('')
      await forgotPassword(data.email)
      setIsSuccess(true)
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          'Failed to send reset email. Please try again.',
      )
    }
  }

  if (isSuccess) {
    return (
      <Box minH="100vh" bg={colors.bg} display="flex" flexDir="column">
        <Box
          flex="1"
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={4}
          py={10}
        >
          <Card
            w="full"
            maxW="md"
            bg={colors.cardBg}
            borderRadius="xl"
            boxShadow="0 8px 10px rgba(0,0,0,0.10)"
          >
            <CardBody p={{ base: 6, md: 8 }}>
              <VStack spacing={4} align="stretch">
                <Box display="flex" justifyContent="center" pt={1}>
                  <Box
                    as="img"
                    src={siteSettings.auth_logo || '/assets/img/als-logo-2.png'}
                    alt={siteSettings.site_name || 'Actionlabs'}
                    maxW="320px"
                    w="100%"
                    objectFit="contain"
                  />
                </Box>

                <Box textAlign="center">
                  <Box display="flex" justifyContent="center" mt={2} mb={2}>
                    <CheckCircle size={64} />
                  </Box>

                  <Heading
                    size="md"
                    color={colors.heading}
                    fontWeight="800"
                    mb={1}
                  >
                    Check Your Email
                  </Heading>

                  <Text fontSize="sm" color={colors.textMuted}>
                    We&apos;ve sent a password reset link to your email address.
                    Please check your inbox and follow the instructions to reset
                    your password.
                  </Text>
                </Box>

                <VStack spacing={3} w="full" pt={2}>
                  <Button
                    type="button"
                    w="full"
                    h="44px"
                    borderRadius="md"
                    color="white"
                    bg={colors.primary}
                    _hover={{ bg: colors.primaryHover }}
                    _active={{ filter: 'brightness(0.95)' }}
                    onClick={() => navigate('/login')}
                  >
                    Back to Login
                  </Button>

                  <Text
                    fontSize="sm"
                    color={colors.textSecondary}
                    textAlign="center"
                  >
                    Didn&apos;t receive the email? Check your spam folder or{' '}
                    <Button
                      variant="link"
                      color={colors.link}
                      fontSize="sm"
                      onClick={() => setIsSuccess(false)}
                      _hover={{
                        color: colors.linkHover,
                        textDecoration: 'underline',
                      }}
                    >
                      try again
                    </Button>
                  </Text>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </Box>

        <Box py={6} textAlign="center">
          <Text fontSize="sm" color={colors.textSecondary}>
            {new Date().getFullYear()} ©{' '}
            <Box as="span" color={colors.email} fontWeight="600">
              Actionlabs
            </Box>
            , All Rights Reserved
          </Text>
        </Box>
      </Box>
    )
  }

  return (
    <Box minH="100vh" bg={colors.bg} display="flex" flexDir="column">
      <Box
        flex="1"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={4}
        py={10}
      >
        <Card
          w="full"
          maxW="448px"
          bg={colors.cardBg}
          borderRadius="xl"
          boxShadow="0 8px 10px rgba(0,0,0,0.10)"
        >
          <CardBody p={{ base: 6, md: 8 }}>
            <VStack spacing={2} align="stretch">
              <Box display="flex" justifyContent="center" pt={1}>
                <Box
                  as="img"
                  src={siteSettings.auth_logo || '/assets/img/als-logo-2.png'}
                  alt={siteSettings.site_name || 'Actionlabs'}
                  maxW="320px"
                  w="100%"
                  objectFit="contain"
                />
              </Box>

              <Box textAlign="center">
                <Heading
                  size="md"
                  color={colors.heading}
                  fontWeight="800"
                  mb={1}
                >
                  Forgot Password?
                </Heading>

                <Text fontSize="sm" color={colors.textMuted}>
                  Enter your email address and we&apos;ll send you a link to
                  <br />
                  reset your password.
                </Text>
              </Box>

              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              <Box as="form" onSubmit={handleSubmit(onSubmit)} w="full">
                <VStack spacing={4} align="stretch">
                  <FormControl isInvalid={!!errors.email}>
                    <FormLabel fontSize="sm" color={colors.heading}>
                      Email Address
                    </FormLabel>

                    <InputGroup>
                      <InputLeftElement
                        pointerEvents="none"
                        color={colors.textMuted}
                      >
                        <Mail size={18} />
                      </InputLeftElement>

                      <Input
                        {...register('email')}
                        type="email"
                        placeholder="Enter your email address"
                        pl="10"
                        bg={colors.inputBg}
                        borderColor={colors.inputBorder}
                        focusBorderColor={colors.email}
                        autoComplete="username"
                      />
                    </InputGroup>

                    <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                  </FormControl>

                  <Button
                    type="submit"
                    w="full"
                    h="44px"
                    borderRadius="md"
                    color="white"
                    bg={colors.primary}
                    _hover={{ bg: colors.primaryHover }}
                    _active={{ filter: 'brightness(0.95)' }}
                    isLoading={isLoading}
                    loadingText="Sending reset link..."
                  >
                    Send Reset Link
                  </Button>

                  {/* Centered EXACTLY like Login / Success state */}
                  <Box pt={1} w="full" position="relative" textAlign="center">
                    <Button
                      variant="link"
                      color={colors.link}
                      fontSize="sm"
                      onClick={() => navigate('/login')}
                      _hover={{
                        color: colors.linkHover,
                        textDecoration: 'underline',
                      }}
                      p={0}
                      minW="unset"
                    >
                      <Box
                        w="full"
                        position="relative"
                        display="inline-block"
                        px={6}
                      >
                        <Box
                          position="absolute"
                          left={0}
                          top="50%"
                          transform="translateY(-50%)"
                        >
                          <ArrowLeft size={16} />
                        </Box>

                        <Text as="span">Back to Login</Text>
                      </Box>
                    </Button>
                  </Box>
                </VStack>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </Box>

      <Box py={6} textAlign="center">
        <Text fontSize="sm" color={colors.textSecondary}>
          {new Date().getFullYear()} ©{' '}
          <Box as="span" color={colors.email} fontWeight="600">
            Actionlabs
          </Box>
          , All Rights Reserved
        </Text>
      </Box>
    </Box>
  )
}
